import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Oval } from 'react-loader-spinner';
import { ImageModel, Style, GeneratedImage } from '../types';
import ImagePreview from './ImagePreview'; // 导入图片预览组件

interface CanvasProps {
  prompt: string;
  selectedModel: ImageModel | null;
  isGenerating: boolean;
  aspectRatio: string;
  previewImages: GeneratedImage[] | null;
  count: number;
  selectedStyle?: Style | null;
  resolution: string; // 添加分辨率属性
}

const Canvas: React.FC<CanvasProps> = ({
  prompt,
  selectedModel,
  isGenerating,
  aspectRatio,
  previewImages,
  count,
  selectedStyle,
  resolution // 添加分辨率属性
}) => {
  // 图片预览状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // 打开图片预览
  const openPreview = (imageUrl: string, index: number = 0) => {
    setPreviewImageUrl(imageUrl);
    setPreviewImageIndex(index);
    setPreviewOpen(true);
  };

  // 关闭图片预览
  const closePreview = () => {
    setPreviewOpen(false);
  };

  // 导航到指定索引的图片
  const navigateToImage = (index: number) => {
    setPreviewImageIndex(index);
  };

  // 根据生成数量获取网格布局样式 - 严格遵循宽高比
  const getGridStyle = () => {
    switch (count) {
      case 2:
        return 'grid-cols-2 gap-3';
      case 3:
        // 对于3张图片，严格保持等宽布局
        return 'grid-cols-3 gap-3';
      case 4:
        return 'grid-cols-2 grid-rows-2 gap-3';
      default:
        return '';
    }
  };

  // 根据生成数量获取图片容器样式 - 严格遵循宽高比
  const getImageContainerStyle = (index: number) => {
    // 根据宽高比设置容器样式
    let aspectStyle = '';
    
    switch (aspectRatio) {
      case '16:9':
        aspectStyle = 'aspect-[16/9]';
        break;
      case '4:3':
        aspectStyle = 'aspect-[4/3]';
        break;
      case '3:4':
        aspectStyle = 'aspect-[3/4]';
        break;
      case '9:16':
        aspectStyle = 'aspect-[9/16]';
        break;
      case '1:1':
      default:
        aspectStyle = 'aspect-square';
        break;
    }
    
    return `w-full h-full ${aspectStyle}`;
  };

  // 获取预览容器样式
  const getPreviewContainerStyle = () => {
    // 对于所有宽高比，使用固定尺寸限制尺寸
    let width: number;
    let height: number;
    
    // 采用新的尺寸策略，确保所有宽高比下都有足够的宽度
    const maxHeight = 750; // 限制最大高度
    const minWidth = 1000;  // 确保最小宽度，增加到1000px
    
    switch (aspectRatio) {
      case '16:9':
        // 横向宽屏比例
        height = maxHeight;
        width = Math.max(minWidth, (16/9) * height);
        break;
      case '4:3':
        // 横向标准比例
        height = maxHeight;
        width = Math.max(minWidth, (4/3) * height);
        break;
      case '3:4':
        // 纵向标准比例 - 增大宽度以提升浏览体验
        height = maxHeight;
        // 对于纵向比例，强制使用最小宽度
        width = minWidth;
        break;
      case '9:16':
        // 纵向窄长比例 - 增大宽度以提升浏览体验
        height = maxHeight;
        // 对于纵向比例，强制使用最小宽度
        width = minWidth;
        break;
      case '1:1':
      default:
        // 正方形比例
        height = maxHeight;
        width = Math.max(minWidth, height);
        break;
    }
    
    return {
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: '100%',
      margin: '0 auto'
    };
  };

  return (
    <div className="flex-1 p-0 pt-3 flex flex-col items-center">
      <div className="w-full max-w-5xl mt-0 mx-auto">
        {/* 预览容器 - 根据宽高比使用不同的样式 */}
        <div 
          className="relative rounded-xl overflow-hidden shadow-art border canvas-container grid-background" 
          style={getPreviewContainerStyle()}
        >
          {/* 背景网格 */}
          <div className="absolute inset-0 bg-dark-800 opacity-50" style={{
            backgroundImage: 'linear-gradient(#374151 1px, transparent 1px), linear-gradient(90deg, #374151 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
          
          {/* 加载动画 */}
          {isGenerating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-800 bg-opacity-70 backdrop-blur-sm">
              <Oval
                height={80}
                width={80}
                color="#0ea5e9"
                secondaryColor="#d946ef"
                strokeWidth={2}
                strokeWidthSecondary={2}
              />
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 text-white font-medium"
              >
                AI正在绘制您的创意...
              </motion.p>
            </div>
          )}

          {/* 预览图片 - 支持多张图片显示 */}
          {previewImages && previewImages.length > 0 && !isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center">
              {count === 1 ? (
                // 单张图片显示
                <img
                  src={previewImages[0].url}
                  alt={`AI生成的图片: ${prompt}`}
                  className="max-w-full max-h-full object-contain cursor-pointer hover:opacity-95 transition-opacity"
                  onClick={() => openPreview(previewImages[0].url)}
                />
              ) : (
                // 多张图片网格显示
                <div className={`grid ${getGridStyle()} w-full h-full`}>
                  {previewImages.map((image, index) => (
                    <div
                      key={index}
                      className={`relative ${getImageContainerStyle(index)} overflow-hidden rounded-lg cursor-pointer hover:opacity-95 transition-opacity`}
                      onClick={() => openPreview(image.url, index)}
                    >
                      <img
                        src={image.url}
                        alt={`AI生成的图片 ${index + 1}: ${prompt}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 提示信息 */}
          {!isGenerating && !prompt && !previewImages && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-3">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-4xl mb-2"
              >
                🎨
              </motion.div>
              <h2 className="text-lg font-semibold theme-text-primary mb-2">准备好创作了吗？</h2>
              <p className="theme-text-secondary text-sm max-w-md">
                选择一个AI模型，输入提示词，然后点击"生成图像"按钮开始创作。
              </p>
            </div>
          )}

          {/* 模型信息 */}
          {selectedModel && !isGenerating && prompt && !previewImages && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <div className="max-w-md">
                <h3 className="text-lg font-medium theme-text-primary mb-2">
                  {selectedModel.name}
                </h3>
                <p className="theme-text-secondary text-sm mb-4">
                  {prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-3">
                  {selectedModel.capabilities.map((capability, index) => (
                    <span key={index} className="px-3 py-1 theme-tag-bg theme-tag-text text-sm">
                      {capability}
                    </span>
                  ))}
                </div>
                
                {/* 显示选择的风格样式 */}
                {selectedStyle && (
                  <div className="mt-3">
                    <span className="text-sm theme-text-primary">风格样式: </span>
                    <span className="px-3 py-1 bg-primary-600 bg-opacity-20 rounded text-primary-400 text-sm">
                      {selectedStyle.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 提示信息 */}
        <div className="mt-1 text-center theme-text-secondary text-xs">
          {isGenerating ? (
            <p>生成可能需要几秒钟时间，请耐心等待...</p>
          ) : (
            <p>图像将在这里预览，生成后可在画廊中查看</p>
          )}
        </div>
      </div>

      {/* 图片预览组件 */}
      <ImagePreview
        imageUrl={previewImageUrl}
        isOpen={previewOpen}
        onClose={closePreview}
        alt={`AI生成的图片: ${prompt}`}
        images={previewImages ? previewImages.map(img => img.url) : []}
        currentIndex={previewImageIndex}
        onNavigate={navigateToImage}
      />
    </div>
  );
};

export default Canvas;
